import { useEffect, useState } from "react";
import {
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography
} from "@material-ui/core";
import {
  Close as CloseIcon,
  ChevronLeft,
  ChevronRight,
  GetApp as DownloadIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon
} from "@material-ui/icons";
import { useTranslation } from "react-i18next";
import useWebotServer from "hooks/useWebotServer";
import { GeneratedUserCreative, MakeoverIntensity, MakeoverSceneType } from "../../../@types/sharedSchema";
import { BeforeAfterSlider } from "./BeforeAfterSlider";
import { MakeoverIntensitySelector } from "./MakeoverIntensitySelector";
import { DEFAULT_MAKEOVER_INTENSITY } from "./makeoverIntensity";
import { getMakeoverEngineIcon } from "./makeoverModelOptions";

export type ComparisonPair = { beforeUrl: string; afterUrl: string };

type ViewMode = "before" | "both" | "after";

async function downloadImage(url: string, suffix: string): Promise<void> {
  if (!url) return;
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = `makeover-${suffix}-${new Date().toISOString()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
  } catch {
    window.open(url, "_blank");
  }
}

type MakeoverComparisonDialogProps = {
  open: boolean;
  pairs: ComparisonPair[];
  index: number;
  onClose: () => void;
  onIndexChange: (index: number) => void;
  /** Show the before/after download buttons. Hidden on public-facing pages. */
  allowDownload?: boolean;
  /**
   * The makeover records behind `pairs` (same order/index). When provided (dashboard
   * only), the dialog shows Retry + Delete actions for the current image.
   */
  makeovers?: GeneratedUserCreative[];
};

/**
 * Modal that shows a makeover one image at a time, with a before / both / after
 * toggle (persists while paging). On the dashboard it also offers Retry (generate
 * another version with a chosen style) and Delete.
 */
export function MakeoverComparisonDialog({
  open,
  pairs,
  index,
  onClose,
  onIndexChange,
  allowDownload = true,
  makeovers
}: MakeoverComparisonDialogProps): JSX.Element | null {
  const { t: translate } = useTranslation();
  const { generateMakeovers, deleteMakeover } = useWebotServer();
  const [viewMode, setViewMode] = useState<ViewMode>("both");
  const [retryOpen, setRetryOpen] = useState(false);
  const [retryStyle, setRetryStyle] = useState("");
  const [retryIntensity, setRetryIntensity] = useState<MakeoverIntensity>(DEFAULT_MAKEOVER_INTENSITY);
  const [retrying, setRetrying] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [pendingNewId, setPendingNewId] = useState<string | null>(null);

  // After a retry, once the new makeover shows up in `makeovers` (via the parent's
  // live listener), jump the dialog to it and show the new image.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!pendingNewId || !makeovers?.length) return;
    const idx = makeovers.findIndex((m) => m.id === pendingNewId);
    if (idx >= 0) {
      onIndexChange(idx);
      setViewMode("after");
      setPendingNewId(null);
    }
  }, [makeovers, pendingNewId]);

  if (!pairs.length) return null;

  const safeIndex = Math.max(0, Math.min(index, pairs.length - 1));
  const pair = pairs[safeIndex];
  const record = makeovers?.[safeIndex];
  const canEdit = !!record?.id; // dashboard mode
  const engineIcon = getMakeoverEngineIcon(record?.makeoverEngine); // tiny badge for the engine that made this makeover
  const hasMany = pairs.length > 1;

  const goTo = (i: number) => {
    setRetryOpen(false);
    onIndexChange((i + pairs.length) % pairs.length);
  };

  const segments: { key: ViewMode; label: string }[] = [
    { key: "before", label: translate("makeovers.before", "לפני") },
    { key: "both", label: translate("makeovers.both", "שניהם") },
    { key: "after", label: translate("makeovers.after", "אחרי") }
  ];

  const openRetry = () => {
    setRetryStyle(record?.makeoverStylePrompt ?? "");
    setRetryIntensity((record?.makeoverIntensity ?? DEFAULT_MAKEOVER_INTENSITY) as MakeoverIntensity);
    setRetryOpen(true);
  };

  const handleRetry = async () => {
    if (!record?.listingId || !record?.sourceImageUrl) return;
    setRetrying(true);
    try {
      const res = await generateMakeovers({
        listingId: record.listingId,
        items: [
          {
            sourceImageUrl: record.sourceImageUrl,
            sceneType: (record.makeoverSceneType ?? "auto") as MakeoverSceneType,
            intensity: retryIntensity,
            stylePrompt: retryStyle.trim() || undefined
          }
        ]
      });
      // Jump to the new makeover once the live listener delivers it (only on success).
      const newId = res?.makeovers?.find((m) => m.status === "completed")?.id ?? null;
      if (newId) setPendingNewId(newId);
      setRetryOpen(false);
    } catch (err) {
      console.error("Failed to retry makeover", err);
    } finally {
      setRetrying(false);
    }
  };

  const handleDelete = async () => {
    if (!record?.id) return;
    setDeleting(true);
    try {
      await deleteMakeover({ makeoverId: record.id });
      setConfirmDeleteOpen(false);
    } catch (err) {
      console.error("Failed to delete makeover", err);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <Box sx={{ position: "relative", p: { xs: 1.5, sm: 2 } }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} flexWrap="wrap" sx={{ mb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {translate("makeovers.compareTitle", "השוואת לפני / אחרי")}
            {hasMany ? ` (${safeIndex + 1}/${pairs.length})` : ""}
          </Typography>
          <Stack direction="row" alignItems="center" gap={1}>
            <Stack direction="row" gap={0.5} sx={{ p: 0.5, borderRadius: 999, backgroundColor: "grey.200" }}>
              {segments.map((s) => (
                <ButtonBase
                  key={s.key}
                  onClick={() => setViewMode(s.key)}
                  sx={{
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 999,
                    fontSize: 13,
                    fontWeight: 600,
                    color: viewMode === s.key ? "common.white" : "grey.800",
                    backgroundColor: viewMode === s.key ? "primary.main" : "transparent",
                    transition: "all 0.2s"
                  }}
                >
                  {s.label}
                </ButtonBase>
              ))}
            </Stack>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>
        </Stack>

        <Box sx={{ position: "relative" }}>
          {viewMode === "both" ? (
            <BeforeAfterSlider key={`${pair.beforeUrl}|${pair.afterUrl}`} beforeUrl={pair.beforeUrl} afterUrl={pair.afterUrl} paddingTop="66%" objectFit="contain" />
          ) : (
            <Box sx={{ position: "relative", width: "100%", paddingTop: "66%", borderRadius: 2, overflow: "hidden", backgroundColor: "grey.200" }}>
              <Box
                component="img"
                src={viewMode === "before" ? pair.beforeUrl : pair.afterUrl}
                alt=""
                sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain" }}
              />
              <Box
                style={{ left: 8 }}
                sx={{ position: "absolute", top: 8, px: 1, py: 0.25, borderRadius: 1, backgroundColor: viewMode === "after" ? "primary.main" : "rgba(0,0,0,0.6)", color: "common.white", display: "flex", alignItems: "center", gap: 0.5 }}
              >
                <Typography variant="caption" sx={{ fontWeight: 700 }}>
                  {viewMode === "before" ? translate("makeovers.before", "לפני") : translate("makeovers.after", "אחרי")}
                </Typography>
                {viewMode === "after" && engineIcon && (
                  <Typography component="span" variant="caption" aria-hidden sx={{ fontSize: 12, lineHeight: 1 }}>
                    {engineIcon}
                  </Typography>
                )}
              </Box>
            </Box>
          )}

          {hasMany && (
            <>
              <IconButton onClick={() => goTo(safeIndex - 1)} style={{ left: 8 }} sx={{ position: "absolute", top: "50%", transform: "translateY(-50%)", backgroundColor: "rgba(255,255,255,0.9)", "&:hover": { backgroundColor: "common.white" } }}>
                <ChevronLeft />
              </IconButton>
              <IconButton onClick={() => goTo(safeIndex + 1)} style={{ right: 8 }} sx={{ position: "absolute", top: "50%", transform: "translateY(-50%)", backgroundColor: "rgba(255,255,255,0.9)", "&:hover": { backgroundColor: "common.white" } }}>
                <ChevronRight />
              </IconButton>
            </>
          )}
        </Box>

        {viewMode === "both" && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block", textAlign: "center" }}>
            {translate("makeovers.explainer.dragHint", "גררו את המחוון כדי להשוות — או הקישו להחלפה")}
          </Typography>
        )}

        {/* Retry panel (dashboard) */}
        {canEdit && retryOpen && (
          <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 2, backgroundColor: "grey.100" }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {translate("makeovers.retryTitle", "יצירת גרסה נוספת")}
            </Typography>
            <Box sx={{ mb: 1.5 }}>
              <MakeoverIntensitySelector value={retryIntensity} onChange={setRetryIntensity} />
            </Box>
            <TextField
              fullWidth
              size="small"
              value={retryStyle}
              onChange={(e) => setRetryStyle(e.target.value)}
              placeholder={translate("makeovers.customStylePlaceholder", "תארו את הסגנון הרצוי, למשל: בוהו שיק עם צמחייה")}
              sx={{ mb: 1, backgroundColor: "common.white" }}
            />
            <Stack direction="row" gap={1} justifyContent="flex-end">
              <Button size="small" onClick={() => setRetryOpen(false)} disabled={retrying}>
                {translate("common.cancel", "ביטול")}
              </Button>
              <Button
                size="small"
                variant="contained"
                color="primary"
                onClick={handleRetry}
                disabled={retrying}
                startIcon={retrying ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
              >
                {translate("makeovers.retryGenerate", "צרו גרסה נוספת")}
              </Button>
            </Stack>
          </Box>
        )}

        {/* Footer actions */}
        {(allowDownload || canEdit) && (
          <Stack direction="row" alignItems="center" justifyContent="center" gap={2} sx={{ mt: 1.5 }} flexWrap="wrap">
            {allowDownload && (
              <>
                <Button variant="contained" color="primary" startIcon={<DownloadIcon />} onClick={() => downloadImage(pair.afterUrl, "after")}>
                  {translate("makeovers.download", "הורדה")}
                </Button>
                <Button variant="text" size="small" onClick={() => downloadImage(pair.beforeUrl, "before")}>
                  {translate("makeovers.downloadBefore", "הורדת תמונת המקור")}
                </Button>
              </>
            )}
            {canEdit && (
              <>
                <Button variant="outlined" color="primary" startIcon={<RefreshIcon />} onClick={openRetry} disabled={retryOpen}>
                  {translate("makeovers.retry", "יצירה מחדש")}
                </Button>
                <Button
                  variant="text"
                  startIcon={<DeleteIcon />}
                  onClick={() => setConfirmDeleteOpen(true)}
                  sx={{ color: "error.main" }}
                >
                  {translate("makeovers.delete", "מחיקה")}
                </Button>
              </>
            )}
          </Stack>
        )}
      </Box>

      <Dialog open={confirmDeleteOpen} onClose={() => !deleting && setConfirmDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{translate("makeovers.deleteConfirmTitle", "מחיקת עיצוב")}</DialogTitle>
        <DialogContent>
          <Typography>{translate("makeovers.deleteConfirmMessage", "למחוק את העיצוב הזה? הוא יוסר גם מהמיני-סייט. לא ניתן לשחזר.")}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteOpen(false)} disabled={deleting}>
            {translate("common.cancel", "ביטול")}
          </Button>
          <Button
            variant="contained"
            onClick={handleDelete}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
            sx={{ backgroundColor: "error.main", color: "error.contrastText", "&:hover": { backgroundColor: "error.dark" } }}
          >
            {translate("makeovers.delete", "מחיקה")}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}

export default MakeoverComparisonDialog;
